import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 mt-4">Terms of Service</h1>
          <p className="text-slate-600 mt-2">Last updated: October 6, 2025</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using StockSentry ("the App"), a Shopify application provided by RetailForge ("we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the App.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Service Description</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              StockSentry provides inventory management and forecasting services for Shopify merchants, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Sales velocity-based inventory alerts</li>
              <li>Seasonal demand forecasting</li>
              <li>Multi-channel notifications (email, SMS, Slack)</li>
              <li>Historical sales analysis</li>
            </ul>
          </section>

          {/* Account Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Account Responsibilities</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You agree to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Provide accurate and complete information during setup</li>
              <li>Maintain the security of your Shopify account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Use the App only for lawful business purposes</li>
              <li>Comply with all applicable laws and Shopify's Terms of Service</li>
            </ul>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Payment and Billing</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong className="text-slate-900">Free Trial:</strong> 14-day trial period with full feature access</li>
              <li><strong className="text-slate-900">Subscription:</strong> Recurring monthly or annual charges via Shopify</li>
              <li><strong className="text-slate-900">Billing:</strong> Charges processed through Shopify's billing system</li>
              <li><strong className="text-slate-900">Cancellation:</strong> Cancel anytime; no refunds for partial months</li>
              <li><strong className="text-slate-900">Price Changes:</strong> 30-day notice for pricing updates</li>
            </ul>
          </section>

          {/* Data and Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data and Privacy</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Your use of StockSentry is also governed by our{' '}
              <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 font-medium underline">
                Privacy Policy
              </Link>
              . Key points:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>We collect only necessary data for inventory management</li>
              <li>We comply with GDPR, CCPA, PIPEDA, and Shopify requirements</li>
              <li>We never sell your data to third parties</li>
              <li>You retain ownership of all your business data</li>
              <li>Data deleted within 30 days of app uninstallation</li>
            </ul>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Acceptable Use</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You may not:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Reverse engineer, decompile, or disassemble the App</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use the App to transmit malware or harmful code</li>
              <li>Violate any laws or regulations</li>
              <li>Interfere with other merchants' use of the App</li>
              <li>Use automated systems to access the App without permission</li>
            </ul>
          </section>

          {/* Service Availability */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Service Availability</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We strive for 99.9% uptime but do not guarantee uninterrupted service. We may:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Perform scheduled maintenance with advance notice</li>
              <li>Temporarily suspend service for security or technical reasons</li>
              <li>Modify or discontinue features with reasonable notice</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              StockSentry, including all code, design, and branding, is owned by RetailForge. You receive a limited, non-exclusive, non-transferable license to use the App during your subscription period.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>StockSentry is provided "as is" without warranties of any kind</li>
              <li>We are not liable for lost profits, revenue, or data</li>
              <li>Our total liability is limited to fees paid in the last 12 months</li>
              <li>We are not responsible for third-party service failures</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong className="text-slate-900">Important:</strong> StockSentry provides alerts and forecasts to assist decision-making. You remain solely responsible for inventory management decisions.
            </p>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Indemnification</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify and hold RetailForge harmless from any claims, damages, or expenses arising from your use of the App, violation of these terms, or infringement of third-party rights.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Termination</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Either party may terminate this agreement:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong className="text-slate-900">By You:</strong> Cancel subscription anytime via Shopify admin</li>
              <li><strong className="text-slate-900">By Us:</strong> For violations of these terms or non-payment</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Upon termination, access ends immediately and data is deleted per our Privacy Policy.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Dispute Resolution</h2>
            <p className="text-slate-600 leading-relaxed">
              Any disputes will be resolved through good-faith negotiation. If unresolved, disputes will be settled through binding arbitration in accordance with applicable laws, with each party bearing their own costs.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              These terms are governed by the laws of the jurisdiction where RetailForge is registered, without regard to conflict of law principles.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Changes to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update these Terms of Service periodically. Significant changes will be communicated via email at least 30 days in advance. Continued use after changes constitutes acceptance.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Contact Information</h2>
            <p className="text-slate-600 leading-relaxed">
              For questions about these Terms of Service:
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong className="text-slate-900">Email:</strong>{' '}
              <a href="mailto:support@retailforge.app" className="text-blue-600 hover:text-blue-700 font-medium underline">
                support@retailforge.app
              </a>
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong className="text-slate-900">Company:</strong> RetailForge
            </p>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Severability</h2>
            <p className="text-slate-600 leading-relaxed">
              If any provision of these terms is found unenforceable, the remaining provisions will continue in full effect.
            </p>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
